import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  dataTip?: string; // Prop per il tooltip
  inline?: boolean; // Prop per la visualizzazione inline
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, inline, size, asChild = false, dataTip: tip, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      inline == false? (
        <div className="absolute right-2 top-2 group inline-block">
          <Comp
            className={cn(buttonVariants({ variant, size, className }), "group")}
            ref={ref}
            {...props}
          />
          {tip && (
            <div className="absolute bottom-0 right-0 mb-9 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <div className="relative bg-gray-700 text-white text-xs px-3 py-2 rounded-md shadow-lg min-w-[120px] max-w-[180px] text-center">
                {tip}
                <div className="absolute bottom-0 right-3 w-0 h-0 border-l-5 border-l-transparent border-t-5 border-t-gray-700 border-r-5 border-r-transparent transform translate-y-full" />
              </div>
            </div>
          )}
        </div>
      ) : (
      <div className="relative group inline-block">
        <Comp
          className={cn(buttonVariants({ variant, size, className }), "group")}
          ref={ref}
          {...props}
        />
        {tip && (
          <div className="absolute bottom-0 right-0 mb-9 mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="relative bg-gray-700 text-white text-xs px-3 py-2 rounded-md shadow-lg min-w-[120px] max-w-[180px] text-center">
              {tip}
              <div className="absolute bottom-0 right-3 w-0 h-0 border-l-5 border-l-transparent border-t-5 border-t-gray-700 border-r-5 border-r-transparent transform translate-y-full" />
            </div>
          </div>
        )}
      </div>
      )
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
